// Стор логов панелей. На каждую сессию — свой срез: строки, состояние связи,
// незакрытые разрешения, черновик ввода и состояние записи голоса.
//
// Ключевое требование производительности: применение delta/rows к строке по seq —
// O(1). Для этого рядом с массивом строк живёт Map seq → позиция; линейного поиска
// по массиву на каждый чанк стрима нет нигде.

import { create } from 'zustand'
import type {
  AttachmentMeta, Line, PastedText, PermissionRequest, ServerMessage, SessionInfo,
} from '../protocol'

export type ConnState = 'connecting' | 'live' | 'reconnecting' | 'dead'
export type PermStatus = 'allowed' | 'denied' | 'expired'
export type VoicePhase = 'idle' | 'requesting' | 'recording' | 'sending' | 'error'

/** Строка лога + клиентская метаинформация (в протокол не входит). */
export interface LogLine extends Line {
  /** строку нарисовал клиент, сервер её не присылал (голосовая пометка, ошибка связи) */
  local?: boolean
  /** оптимистичное эхо ввода; ждёт подтверждения строкой от сервера (PROTOCOL §4) */
  clientId?: string
}

/** Файл, прикреплённый к неотправленному сообщению.
 *
 *  Живёт в срезе сессии рядом с `draft`: чип не должен пропадать при
 *  переключении раскладок и пересборке панели, а загрузка на сервер идёт
 *  дольше, чем живёт локальный state компонента. */
export interface DraftAttachment {
  /** локальный id чипа; сервер про него не знает */
  id: string
  name: string
  size: number
  /** путь в каталоге сессии; появляется, когда файл долетел */
  path: string | null
  status: 'uploading' | 'ready' | 'error'
  error: string | null
  /** id на сервере — уходит в input.attachments (новый сервер) */
  serverId?: string | null
  /** 0..1, пока файл летит (§2в п.2: полоса по нижнему краю плашки) */
  progress?: number
  mime?: string
  kind?: 'image' | 'file'
  executable?: boolean
  /** локальное превью картинки (object URL) — до и после загрузки */
  preview?: string | null
}

/** Файлы для повторной загрузки («повторить») — не в сторе: File не сериализуется. */
export const attachmentFiles = new Map<string, File>()

export interface VoiceState {
  phase: VoicePhase
  /** длительность записи в секундах */
  seconds: number
  /** 28 полос эквалайзера, высоты 3..18 px */
  bars: number[]
  error: string | null
  /** название микрофона, с которого идёт запись */
  device: string
  /** предупреждение во время записи («микрофон молчит») */
  warn: string | null
}

export interface SessionSlice {
  lines: LogLine[]
  /** seq → позиция в lines; мутируется на месте, в рендер не попадает */
  index: Map<number, number>
  /** последний СЕРВЕРНЫЙ seq — он же since при переподключении */
  seq: number
  /** счётчик локальных строк, уходит в минус, чтобы не пересекаться с серверными */
  localSeq: number
  session: SessionInfo | null
  conn: ConnState
  rtt: number | null
  /** незакрытые запросы разрешений (из hello и из сообщений perm) */
  pending: PermissionRequest[]
  /** id запроса → чем он кончился */
  permStatus: Record<string, PermStatus>
  draft: string
  /** вложения этого же черновика */
  attachments: DraftAttachment[]
  voice: VoiceState
  /** последняя ошибка транспорта, показывается в шапке */
  error: string | null
  /** hello уже приходил — значит списку pending можно верить */
  greeted: boolean
}

export const VOICE_BARS = 28

const emptyVoice = (): VoiceState => ({
  phase: 'idle', seconds: 0, bars: new Array(VOICE_BARS).fill(3), error: null,
  device: '', warn: null,
})

const emptySlice = (): SessionSlice => ({
  lines: [],
  index: new Map(),
  seq: 0,
  localSeq: 0,
  session: null,
  conn: 'connecting',
  rtt: null,
  pending: [],
  permStatus: {},
  draft: '',
  attachments: [],
  voice: emptyVoice(),
  error: null,
  greeted: false,
})

/** Стабильная пустая ссылка — чтобы селекторы не порождали новые объекты. */
export const EMPTY_SLICE: SessionSlice = emptySlice()

// ---------------------------------------------------------------------------
// Мост в appStore (оболочка). Панель ТОЛЬКО пишет состояние связи и RTT;
// читать appStore и тем более менять его она не имеет права.
// Оболочка регистрирует приёмник один раз при старте: setConnSink(fn).
// ---------------------------------------------------------------------------

export type ConnSink = (sessionId: string, conn: ConnState, rtt: number | null) => void

let connSink: ConnSink | null = null

export function setConnSink(sink: ConnSink | null): void {
  connSink = sink
}

function notifyConn(id: string, slice: SessionSlice): void {
  if (!connSink) return
  try {
    connSink(id, slice.conn, slice.rtt)
  } catch {
    /* оболочка не должна ронять панель */
  }
}

// ---------------------------------------------------------------------------

interface LogState {
  sessions: Record<string, SessionSlice>
  /** раскрытие блоков вывода — на КОНКРЕТНУЮ строку: `${sessionId}:${seq}` */
  expanded: Record<string, boolean>

  ensure(id: string): void
  drop(id: string): void

  /** пачка серверных сообщений за один кадр — один set, один рендер */
  applyMany(id: string, msgs: ServerMessage[]): void

  setConn(id: string, conn: ConnState): void
  setRtt(id: string, rtt: number | null): void
  setError(id: string, error: string | null): void

  /** оптимистичное эхо ввода до подтверждения сервером */
  pushOptimistic(id: string, text: string, clientId: string, attachments?: AttachmentMeta[], pastes?: PastedText[]): void
  /** чисто клиентская строка (пометка о голосовом, ошибка транспорта) */
  pushLocal(id: string, line: Omit<LogLine, 'seq' | 'ts' | 'local'>): void

  setDraft(id: string, draft: string): void

  /** чипы появляются сразу, ещё до ответа сервера — оператор видит, что файл взят */
  addAttachments(id: string, items: DraftAttachment[]): void
  patchAttachment(id: string, attachmentId: string, patch: Partial<DraftAttachment>): void
  removeAttachment(id: string, attachmentId: string): void
  clearAttachments(id: string): void

  toggleExpanded(id: string, seq: number): void

  /** локальная отметка ответа на разрешение — до прихода perm_resolved */
  markPermAnswered(id: string, permId: string, status: PermStatus): void

  patchVoice(id: string, patch: Partial<VoiceState>): void
  resetVoice(id: string): void

  lastSeq(id: string): number
}

/** Достать срез и гарантировать, что он существует. */
function withSlice(
  state: LogState,
  id: string,
  fn: (slice: SessionSlice) => SessionSlice | null,
): Partial<LogState> | null {
  const cur = state.sessions[id] ?? emptySlice()
  const next = fn(cur)
  if (!next) return null
  return { sessions: { ...state.sessions, [id]: next } }
}

/** Удалить строку и переиндексировать хвост (нужно только при склейке эха). */
function removeAt(lines: LogLine[], index: Map<number, number>, pos: number): void {
  index.delete(lines[pos].seq)
  lines.splice(pos, 1)
  for (let i = pos; i < lines.length; i++) index.set(lines[i].seq, i)
}

export const useLogStore = create<LogState>((set, get) => ({
  sessions: {},
  expanded: {},

  ensure(id) {
    if (get().sessions[id]) return
    set((s) => (s.sessions[id] ? {} : { sessions: { ...s.sessions, [id]: emptySlice() } }))
  },

  drop(id) {
    set((s) => {
      if (!s.sessions[id]) return {}
      const sessions = { ...s.sessions }
      delete sessions[id]
      // чистим раскрытия этой сессии, иначе Record растёт вечно
      const prefix = `${id}:`
      const expanded: Record<string, boolean> = {}
      for (const key of Object.keys(s.expanded)) {
        if (!key.startsWith(prefix)) expanded[key] = s.expanded[key]
      }
      return { sessions, expanded }
    })
  },

  applyMany(id, msgs) {
    if (msgs.length === 0) return
    set((state) => {
      const patch = withSlice(state, id, (cur) => {
        const index = cur.index
        let lines = cur.lines
        let copied = false
        const mutable = (): LogLine[] => {
          if (!copied) { lines = lines.slice(); copied = true }
          return lines
        }

        let seq = cur.seq
        let localSeq = cur.localSeq
        let session = cur.session
        let pending = cur.pending
        let permStatus = cur.permStatus
        let greeted = cur.greeted
        let conn = cur.conn
        let error = cur.error

        const push = (line: LogLine): void => {
          const arr = mutable()
          index.set(line.seq, arr.length)
          arr.push(line)
        }

        const replaceAt = (pos: number, line: LogLine): void => {
          mutable()[pos] = line
        }

        for (const msg of msgs) {
          switch (msg.type) {
            case 'hello': {
              session = msg.session
              pending = msg.pendingPermissions
              greeted = true
              // неподтверждённые эхо-строки сервер сейчас перешлёт заново
              const arr = mutable()
              for (let i = arr.length - 1; i >= 0; i--) {
                if (arr[i].clientId && arr[i].seq < 0) removeAt(arr, index, i)
              }
              break
            }
            case 'line': {
              const line = msg.line as LogLine
              const known = index.get(line.seq)
              if (known !== undefined) {
                const prev = lines[known]
                const merged = { ...prev, ...line }
                // снимок partial-строки может отстать от уже накопленного текста
                // (второй поток, реплей после реконнекта) — не укорачиваем его
                if (
                  prev.partial && line.partial &&
                  (prev.text ?? '').length > (line.text ?? '').length &&
                  (prev.text ?? '').startsWith(line.text ?? '')
                ) {
                  merged.text = prev.text
                }
                replaceAt(known, merged)
              } else {
                if (line.kind === 'me') {
                  // склейка по clientId: серверная строка вытесняет оптимистичную.
                  // Новый сервер возвращает clientId в строке; старый — нет,
                  // тогда ищем самое старое неподтверждённое эхо с тем же текстом.
                  const arr = mutable()
                  const want = (line.text ?? '').trim()
                  let pos = -1
                  for (let i = 0; i < arr.length && line.clientId; i++) {
                    if (arr[i].seq < 0 && arr[i].clientId === line.clientId) { pos = i; break }
                  }
                  for (let i = 0; i < arr.length && pos === -1; i++) {
                    if (arr[i].seq < 0 && arr[i].clientId && (arr[i].text ?? '').trim() === want) {
                      pos = i
                    }
                    if (pos !== -1) break
                  }
                  if (pos !== -1) removeAt(arr, index, pos)
                }
                push(line)
              }
              if (line.seq > seq) seq = line.seq
              break
            }
            case 'delta': {
              const pos = index.get(msg.seq)
              if (pos !== undefined) {
                const prev = lines[pos]
                const have = prev.text ?? ''
                let append = msg.append
                // offset — длина текста до этой дописки на сервере. Если у нас уже
                // длиннее, часть (или вся) дописка доставлена раньше — второй
                // поток, гонка «подписка → снимок истории»: берём только хвост.
                if (typeof msg.offset === 'number' && have.length > msg.offset) {
                  append = append.slice(have.length - msg.offset)
                }
                if (append) {
                  replaceAt(pos, { ...prev, text: have + append, partial: true })
                }
              }
              break
            }
            case 'rows': {
              const pos = index.get(msg.seq)
              if (pos !== undefined) {
                const prev = lines[pos]
                replaceAt(pos, {
                  ...prev,
                  rows: prev.rows ? prev.rows.concat(msg.append) : msg.append.slice(),
                  partial: true,
                })
              }
              break
            }
            case 'done': {
              const pos = index.get(msg.seq)
              if (pos !== undefined) {
                replaceAt(pos, {
                  ...lines[pos],
                  partial: false,
                  ...(msg.exitCode !== undefined ? { exitCode: msg.exitCode } : {}),
                })
              }
              break
            }
            case 'status':
              session = msg.session
              break
            case 'perm': {
              const req = msg.request
              pending = pending.some((p) => p.id === req.id) ? pending : [...pending, req]
              break
            }
            case 'perm_resolved': {
              pending = pending.filter((p) => p.id !== msg.id)
              permStatus = { ...permStatus, [msg.id]: msg.status }
              break
            }
            case 'error': {
              error = msg.message
              if (msg.fatal) conn = 'dead'
              localSeq -= 1
              push({
                seq: localSeq, ts: Date.now() / 1000, kind: 'warn',
                text: msg.message, local: true,
              })
              break
            }
            case 'pong':
              // RTT считает транспорт: он знает время отправки
              break
          }
        }

        return {
          ...cur,
          lines, index, seq, localSeq, session, pending, permStatus, greeted, conn, error,
        }
      })
      return patch ?? {}
    })
  },

  setConn(id, conn) {
    set((state) => {
      const patch = withSlice(state, id, (cur) => {
        if (cur.conn === conn) return null
        const next = { ...cur, conn, rtt: conn === 'live' ? cur.rtt : null }
        notifyConn(id, next)
        return next
      })
      return patch ?? {}
    })
  },

  setRtt(id, rtt) {
    set((state) => {
      const patch = withSlice(state, id, (cur) => {
        if (cur.rtt === rtt) return null
        const next = { ...cur, rtt }
        notifyConn(id, next)
        return next
      })
      return patch ?? {}
    })
  },

  setError(id, error) {
    set((state) => withSlice(state, id, (cur) => (
      cur.error === error ? null : { ...cur, error }
    )) ?? {})
  },

  pushOptimistic(id, text, clientId, attachments, pastes) {
    set((state) => withSlice(state, id, (cur) => {
      const seq = cur.localSeq - 1
      const line: LogLine = {
        seq, ts: Date.now() / 1000, kind: 'me', text, clientId,
        ...(attachments?.length ? { attachments } : {}),
        ...(pastes?.length ? { pastes } : {}),
      }
      const lines = cur.lines.slice()
      cur.index.set(seq, lines.length)
      lines.push(line)
      return { ...cur, lines, localSeq: seq }
    }) ?? {})
  },

  pushLocal(id, line) {
    set((state) => withSlice(state, id, (cur) => {
      const seq = cur.localSeq - 1
      const lines = cur.lines.slice()
      cur.index.set(seq, lines.length)
      lines.push({ ...line, seq, ts: Date.now() / 1000, local: true })
      return { ...cur, lines, localSeq: seq }
    }) ?? {})
  },

  setDraft(id, draft) {
    set((state) => withSlice(state, id, (cur) => (
      cur.draft === draft ? null : { ...cur, draft }
    )) ?? {})
  },

  addAttachments(id, items) {
    if (items.length === 0) return
    set((state) => withSlice(state, id, (cur) => ({
      ...cur, attachments: [...cur.attachments, ...items],
    })) ?? {})
  },

  patchAttachment(id, attachmentId, patch) {
    set((state) => withSlice(state, id, (cur) => {
      // ответ мог прийти после того, как оператор убрал чип — тогда молчим
      if (!cur.attachments.some((a) => a.id === attachmentId)) return null
      return {
        ...cur,
        attachments: cur.attachments.map((a) => (
          a.id === attachmentId ? { ...a, ...patch } : a
        )),
      }
    }) ?? {})
  },

  removeAttachment(id, attachmentId) {
    set((state) => withSlice(state, id, (cur) => {
      const attachments = cur.attachments.filter((a) => a.id !== attachmentId)
      return attachments.length === cur.attachments.length ? null : { ...cur, attachments }
    }) ?? {})
  },

  clearAttachments(id) {
    set((state) => withSlice(state, id, (cur) => (
      cur.attachments.length === 0 ? null : { ...cur, attachments: [] }
    )) ?? {})
  },

  toggleExpanded(id, seq) {
    const key = `${id}:${seq}`
    set((s) => ({ expanded: { ...s.expanded, [key]: !s.expanded[key] } }))
  },

  markPermAnswered(id, permId, status) {
    set((state) => withSlice(state, id, (cur) => ({
      ...cur,
      pending: cur.pending.filter((p) => p.id !== permId),
      permStatus: { ...cur.permStatus, [permId]: status },
    })) ?? {})
  },

  patchVoice(id, patch) {
    set((state) => withSlice(state, id, (cur) => ({
      ...cur, voice: { ...cur.voice, ...patch },
    })) ?? {})
  },

  resetVoice(id) {
    set((state) => withSlice(state, id, (cur) => ({ ...cur, voice: emptyVoice() })) ?? {})
  },

  lastSeq(id) {
    return get().sessions[id]?.seq ?? 0
  },
}))

/** Императивный доступ для транспорта (вне React). */
export const logStore = useLogStore

/** Хук на срез одной сессии. Селектор обязан возвращать стабильные значения. */
export function usePane<T>(sessionId: string, selector: (slice: SessionSlice) => T): T {
  return useLogStore((s) => selector(s.sessions[sessionId] ?? EMPTY_SLICE))
}

/** Раскрыт ли блок вывода конкретной строки. */
export function useExpanded(sessionId: string, seq: number): boolean {
  return useLogStore((s) => !!s.expanded[`${sessionId}:${seq}`])
}
